// NavBar.tsx — site-wide unified nav for Protein Pal.
//
// Mount once at the top of Root.tsx, above whichever screen the current route
// picks. The bar is fixed-position (top center), z-index 50, so it floats over
// all screens. Reads route from useRoute(), drives navigation via setRoute(),
// drives language via i18n.changeLanguage(), drives sign-out via useAuth().
//
// Visual identity copied verbatim from the Portal.tsx aesthetic (dark cosmic
// purple, wavy canvas backdrop, accent palette already in use across the app).
//
// Setup before drop-in:
//   1) npm i @phosphor-icons/react
//   2) Copy lil-pal.svg next to NavBar.tsx, OR adjust the import path.
//   3) Merge i18n-additions.json keys into i18n/locales/{en,ko}.json.
//   4) Replace src/index.tsx Root with the one in Root.tsx (this folder).

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../AuthProvider";
import {
  RocketLaunch, Flask, Package, ChartBar, Database, Grains, Notebook,
  Translate, UserCircle, ShieldStar, SignOut, Info, X, Warning, IconWeight,
  Scales,
} from "@phosphor-icons/react";

import { Route, useRoute } from "./useRoute";
import {
  PRIMARY_ITEMS, META_ITEMS, NavItemDef, PhosphorIconName, tr,
} from "./navItems";
// @ts-ignore — picked up by CRA / Vite asset pipeline
import lilPalSvg from "./lil-pal.svg";

// ── Icon registry ──────────────────────────────────────────────────────────
const ICONS: Record<PhosphorIconName, React.ComponentType<any>> = {
  RocketLaunch, Flask, Package, ChartBar, Database, Grains, Notebook,
  Translate, UserCircle, ShieldStar, SignOut, Scales,
};

// ── Sizing constants ───────────────────────────────────────────────────────
// Final design tokens — locked in 2026-01-30.
// Layout: top, grouping: on, tone: vivid, weight: duotone, 55px, 30% corner.
const SQ = 55;
const RADIUS = SQ * 0.30;     // squircle corner (30%)
const RADIUS_TIGHT = RADIUS * 0.35; // joined corner inside a group
const ICON_PX = Math.round(SQ * 0.52);

// ── Lang helper for the language pill ──────────────────────────────────────
function currentLang(i18n: ReturnType<typeof useTranslation>["i18n"]): "en" | "ko" {
  return i18n.language === "ko" ? "ko" : "en";
}

// ── BrandChip (Lil Pal mascot) ─────────────────────────────────────────────
function BrandChip({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      aria-label="Protein Pal — home"
      className="pp-brand-chip"
      style={{
        width: SQ, height: SQ, borderRadius: SQ * 0.3,
        background: "radial-gradient(circle at 30% 30%, rgba(196,181,253,0.25), rgba(15,11,42,0.6))",
        border: "1px solid rgba(139,92,246,0.45)",
        overflow: "hidden", position: "relative",
        padding: 0, cursor: "pointer", flexShrink: 0,
      }}
    >
      <img
        src={lilPalSvg as unknown as string}
        alt=""
        draggable={false}
        className="pp-brand-lil-pal"
        style={{
          height: "78%", width: "auto",
          objectFit: "contain", objectPosition: "center bottom",
          position: "absolute", bottom: 4, left: "50%",
          transform: "translateX(-50%)",
          transformOrigin: "50% 95%",
          pointerEvents: "none",
        }}
      />
    </button>
  );
}

// ── NavItem ────────────────────────────────────────────────────────────────
type GroupPos = "solo" | "parent" | "child-mid" | "child-end";

function navItemRadii(groupPos: GroupPos): string {
  let tl = RADIUS, tr_ = RADIUS, br = RADIUS, bl = RADIUS;
  if (groupPos === "parent")        { tr_ = RADIUS_TIGHT; br = RADIUS_TIGHT; }
  else if (groupPos === "child-mid"){ tl = tr_ = br = bl = RADIUS_TIGHT; }
  else if (groupPos === "child-end"){ tl = RADIUS_TIGHT; bl = RADIUS_TIGHT; }
  return `${tl}px ${tr_}px ${br}px ${bl}px`;
}

function NavItem({
  item, active, onClick, onInfo, groupPos, joinPrev, lang,
  labelOverride, descOverride,
}: {
  item: NavItemDef;
  active: boolean;
  onClick: () => void;
  onInfo?: (item: NavItemDef) => void;
  groupPos: GroupPos;
  joinPrev: boolean;
  lang: "en" | "ko";
  labelOverride?: string;
  descOverride?: string;
}) {
  const Icon = ICONS[item.icon];
  const [hovered, setHovered] = useState(false);
  const label = labelOverride ?? tr(item.label, lang);
  const desc  = descOverride  ?? tr(item.desc,  lang);
  const accent = item.color;

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      aria-label={label}
      className={`pp-nav-item ${active ? "active" : ""}`}
      style={{
        position: "relative",
        width: SQ, height: SQ,
        borderRadius: navItemRadii(groupPos),
        background: active ? accent : hovered ? `${accent}2e` : "rgba(255,255,255,0.03)",
        border: active ? "1px solid transparent" : `1px solid ${hovered ? `${accent}80` : "rgba(139,92,246,0.22)"}`,
        color: active ? "#0a0618" : hovered ? "#fff" : "#c4b5fd",
        cursor: "pointer", padding: 0, outline: "none",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        transition: "transform .18s cubic-bezier(.2,.7,.2,1.4), background .2s, border-color .2s, color .2s, box-shadow .2s",
        transform: hovered ? "translateY(-2px)" : "translateY(0)",
        boxShadow: active
          ? `0 8px 24px -8px ${accent}, inset 0 0 0 1px rgba(255,255,255,0.06), inset 0 -8px 18px -8px rgba(255,255,255,0.5)`
          : "none",
        marginLeft: joinPrev ? -10 : 0,
      }}
    >
      <Icon size={ICON_PX} weight="duotone" color="currentColor" />

      {/* Hairline join between attached parent + child */}
      {joinPrev && !active && (
        <span
          aria-hidden
          style={{
            position: "absolute",
            top: "50%", left: 0, transform: "translate(-50%, -50%)",
            width: 1, height: SQ * 0.5,
            background: "rgba(139,92,246,0.35)",
            pointerEvents: "none",
          }}
        />
      )}

      {/* Active under-pill */}
      {active && (
        <span
          aria-hidden
          style={{
            position: "absolute",
            left: "50%", bottom: -10, transform: "translateX(-50%)",
            width: SQ * 0.4, height: 3,
            background: accent, borderRadius: 999,
            boxShadow: `0 0 16px ${accent}`,
          }}
        />
      )}

      {/* Tooltip */}
      <span
        role="tooltip"
        style={{
          position: "absolute",
          top: "calc(100% + 10px)", left: "50%",
          transform: `translate(-50%, ${hovered ? "0" : "-2px"})`,
          opacity: hovered ? 1 : 0,
          pointerEvents: "none",
          background: "rgba(8,5,24,0.96)",
          border: "1px solid rgba(139,92,246,0.35)",
          borderRadius: 10, padding: "8px 12px",
          whiteSpace: "nowrap",
          fontSize: 12, fontWeight: 500,
          color: "#c4b5fd",
          transition: "opacity .14s ease, transform .14s ease",
          zIndex: 100,
          boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
          backdropFilter: "blur(12px)",
        }}
      >
        <span style={{ color: "#fff", fontWeight: 700, fontSize: 12 }}>{label}</span>
        {desc && (
          <div style={{ color: "#94a3b8", fontSize: 11, marginTop: 2, fontWeight: 500 }}>{desc}</div>
        )}
      </span>

      {/* On-corner (i) info badge — just the outlined Phosphor info glyph */}
      {item.info && (
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => { e.stopPropagation(); onInfo?.(item); }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault(); e.stopPropagation(); onInfo?.(item);
            }
          }}
          aria-label={lang === "ko" ? "자세히 보기" : "More info"}
          title={lang === "ko" ? "자세히 보기" : "More info"}
          style={{
            position: "absolute",
            top: -3,
            ...(groupPos === "parent" ? { right: "auto", left: -3 } : { right: -3 }),
            background: "transparent", border: "none", padding: 0,
            color: active ? "rgba(10,6,24,0.55)" : "rgba(196,181,253,0.50)",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", zIndex: 5,
            transition: "color .15s ease",
          }}
        >
          <Info size={14} weight="regular" />
        </span>
      )}
    </button>
  );
}

// ── Divider between groups ─────────────────────────────────────────────────
function Divider() {
  return (
    <span
      style={{
        width: 1, height: 24,
        background: "linear-gradient(180deg, transparent, rgba(139,92,246,0.4), transparent)",
        margin: "0 4px", flexShrink: 0,
      }}
    />
  );
}

// ── Info modal ─────────────────────────────────────────────────────────────
function InfoModal({ item, lang, onClose }: { item: NavItemDef; lang: "en" | "ko"; onClose: () => void }) {
  const Icon = ICONS[item.icon];
  const accent = item.color;
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(5,3,15,0.7)", backdropFilter: "blur(10px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 110, padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "rgba(15,11,42,0.92)",
          border: `1px solid ${accent}55`,
          borderRadius: 24, padding: 32,
          width: 520, maxWidth: "100%",
          boxShadow: `0 30px 80px rgba(0,0,0,0.6), 0 0 60px ${accent}22`,
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
          <div
            style={{
              width: 56, height: 56, borderRadius: 17,
              background: accent, color: "#0a0618",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
              boxShadow: `0 8px 24px -8px ${accent}`,
            }}
          >
            <Icon size={30} weight="duotone" />
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "#fff", letterSpacing: "-0.3px" }}>
              {tr(item.label, lang)}
            </h2>
            <p style={{ margin: "4px 0 0", color: "#94a3b8", fontSize: 13 }}>{tr(item.desc, lang)}</p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: "transparent",
              border: "1px solid rgba(139,92,246,0.22)",
              borderRadius: 10, width: 34, height: 34,
              color: "#94a3b8", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <X size={18} weight="regular" />
          </button>
        </div>

        <div style={{ color: "#e2e8f0", fontSize: 14, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
          {tr(item.info, lang)}
        </div>

        {item.warning && (
          <div
            style={{
              marginTop: 20, padding: "12px 14px",
              background: "rgba(252, 211, 77, 0.08)",
              border: "1px solid rgba(252, 211, 77, 0.3)",
              borderRadius: 12,
              display: "flex", gap: 10, alignItems: "flex-start",
            }}
          >
            <Warning size={18} weight="fill" color="#fcd34d" style={{ flexShrink: 0, marginTop: 1 }} />
            <div style={{ color: "#fde68a", fontSize: 12, lineHeight: 1.55 }}>
              <strong style={{ color: "#fcd34d" }}>{lang === "ko" ? "참고" : "Note"}</strong> — {tr(item.warning, lang)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Sign-out confirm modal ─────────────────────────────────────────────────
function SignOutModal({ onConfirm, onClose }: { onConfirm: () => void; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(5,3,15,0.6)", backdropFilter: "blur(8px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 100,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "rgba(15,11,42,0.92)",
          border: "1px solid rgba(139,92,246,0.45)",
          borderRadius: 20, padding: 28,
          width: 360, textAlign: "center",
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}
      >
        <SignOut size={48} weight="duotone" color="#fb7185" />
        <h3 style={{ margin: "12px 0 4px", color: "#fff" }}>{t("globalNav.signoutModal.title")}</h3>
        <p style={{ color: "#94a3b8", fontSize: 14, margin: "0 0 20px" }}>{t("globalNav.signoutModal.body")}</p>
        <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
          <button
            onClick={onClose}
            style={{
              padding: "10px 20px", borderRadius: 10,
              border: "1px solid rgba(139,92,246,0.22)",
              background: "transparent", color: "#fff",
              cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap",
            }}
          >
            {t("globalNav.signoutModal.cancel")}
          </button>
          <button
            onClick={onConfirm}
            style={{
              padding: "10px 20px", borderRadius: 10, border: "none",
              background: "linear-gradient(135deg, #fb7185, #f43f5e)",
              color: "#fff", cursor: "pointer", fontWeight: 700, whiteSpace: "nowrap",
            }}
          >
            {t("globalNav.signoutModal.confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── NavBar (default export) ────────────────────────────────────────────────
export default function NavBar() {
  const { i18n, t } = useTranslation();
  const { isAdmin, signOut } = useAuth();
  const [route, navigate] = useRoute();
  const [signOutOpen, setSignOutOpen] = useState(false);
  const [infoItem, setInfoItem] = useState<NavItemDef | null>(null);
  const lang = currentLang(i18n);

  const handleClick = (id: NavItemDef["id"]) => {
    if (id === "language") {
      const next = lang === "ko" ? "en" : "ko";
      i18n.changeLanguage(next);
      localStorage.setItem("proteinpal_lang", next);
      return;
    }
    if (id === "signout") {
      setSignOutOpen(true);
      return;
    }
    navigate(id as Route);
  };

  // Walk PRIMARY_ITEMS and assign groupPos / joinPrev for the joined capsule.
  const primaryWalk: Array<{ item: NavItemDef; groupPos: GroupPos; joinPrev: boolean }> = [];
  const byParent: Record<string, NavItemDef[]> = {};
  const parents: NavItemDef[] = [];
  PRIMARY_ITEMS.forEach((it) => {
    if (!it.parent) parents.push(it);
    else (byParent[it.parent] = byParent[it.parent] || []).push(it);
  });
  parents.forEach((p) => {
    const kids = byParent[p.id] || [];
    if (kids.length === 0) {
      primaryWalk.push({ item: p, groupPos: "solo", joinPrev: false });
    } else {
      primaryWalk.push({ item: p, groupPos: "parent", joinPrev: false });
      kids.forEach((k, i) => {
        primaryWalk.push({
          item: k,
          groupPos: i === kids.length - 1 ? "child-end" : "child-mid",
          joinPrev: true,
        });
      });
    }
  });

  const meta = META_ITEMS.filter((m) => !m.adminOnly || isAdmin);
  const langItem = META_ITEMS[0];
  const langLabel = lang === "ko" ? "언어 전환 · 한국어" : "Switch Language · EN";
  const langDesc  = lang === "ko"
    ? "현재 한국어 — English로 전환하려면 클릭."
    : "Currently English — click for 한국어.";

  return (
    <>
      {/* Fixed top-center pill */}
      <div
        className="pp-navbar"
        style={{
          position: "fixed",
          top: 20, left: "50%", transform: "translateX(-50%)",
          zIndex: 50,
          display: "flex", alignItems: "center", gap: 10,
          padding: 10,
          background: "rgba(15,11,42,0.72)",
          border: "1px solid rgba(139,92,246,0.28)",
          borderRadius: SQ + 16,
          backdropFilter: "blur(20px)",
          boxShadow: "0 20px 60px rgba(0,0,0,0.5), inset 0 0 0 1px rgba(255,255,255,0.04)",
        }}
      >
        <BrandChip onClick={() => navigate("launchpad")} />
        <Divider />

        {primaryWalk.map(({ item, groupPos, joinPrev }) => (
          <NavItem
            key={item.id}
            item={item}
            active={route === item.id}
            onClick={() => handleClick(item.id)}
            onInfo={setInfoItem}
            groupPos={groupPos}
            joinPrev={joinPrev}
            lang={lang}
          />
        ))}

        <Divider />

        <NavItem
          item={langItem}
          active={false}
          onClick={() => handleClick("language")}
          groupPos="solo"
          joinPrev={false}
          lang={lang}
          labelOverride={langLabel}
          descOverride={langDesc}
        />
        {meta.slice(1).map((it) => (
          <NavItem
            key={it.id}
            item={it}
            active={route === it.id}
            onClick={() => handleClick(it.id)}
            onInfo={setInfoItem}
            groupPos="solo"
            joinPrev={false}
            lang={lang}
          />
        ))}
      </div>

      {/* Lil Pal wave-on-hover animation. Scoped to NavBar via class. */}
      <style>{`
        @keyframes pp-lil-pal-wave {
          0%   { transform: translateX(-50%) rotate(0deg); }
          15%  { transform: translateX(-50%) rotate(-12deg); }
          35%  { transform: translateX(-50%) rotate(10deg); }
          55%  { transform: translateX(-50%) rotate(-8deg); }
          75%  { transform: translateX(-50%) rotate(6deg); }
          100% { transform: translateX(-50%) rotate(0deg); }
        }
        .pp-brand-chip:hover .pp-brand-lil-pal {
          animation: pp-lil-pal-wave 0.8s ease-in-out;
        }
        .pp-brand-chip { transition: transform 0.18s ease; }
        .pp-brand-chip:hover { transform: translateY(-1px); }

        /* Hide on print — print-only views (FormulaReport) shouldn't show the bar. */
        @media print { .pp-navbar { display: none !important; } }
      `}</style>

      {infoItem && <InfoModal item={infoItem} lang={lang} onClose={() => setInfoItem(null)} />}

      {signOutOpen && (
        <SignOutModal
          onClose={() => setSignOutOpen(false)}
          onConfirm={async () => {
            setSignOutOpen(false);
            await signOut();
          }}
        />
      )}
    </>
  );
}
